import React, { FC, useMemo, useState } from 'react'
import styles from './styles.module.css'
import { FaChevronLeft, FaChevronRight, FaEllipsis } from 'react-icons/fa6'
import debounce from 'lodash.debounce'
import Link from 'next/link'
import { useRouter } from 'next/router'

import { addLocalePrefix } from 'lib/addLocalePrefix'

type Props = {
  page: number
  total: number
  onChangePage: (page: number) => void
}

export const Pagination: FC<Props> = ({ page, total, onChangePage }) => {
  const [currentPage, setCurrentPage] = useState(page)
  const router = useRouter()

  const handleChangePage = useMemo(() => {
    return debounce((selectedPage) => {
      onChangePage(selectedPage)
    }, 500)
  }, [onChangePage])

  const getPageUrl = (pageNum: number): string => {
    const currentPath = router.asPath.split('?')[0]

    // router.queryからNext.jsの動的ルートパラメータ（country, slugなど）を除外
    const { country, slug, ...actualQueryParams } = router.query

    if (pageNum === 1) {
      delete actualQueryParams.page
    } else {
      actualQueryParams.page = pageNum.toString()
    }

    const queryString =
      Object.keys(actualQueryParams).length > 0
        ? '?' + new URLSearchParams(actualQueryParams as Record<string, string>).toString()
        : ''

    return addLocalePrefix(`${currentPath}${queryString}`, router.locale)
  }

  const renderPaging = useMemo(() => {
    if (total <= 1) return null

    const renderPage = (pageIndex: number) => (
      <Page
        key={pageIndex}
        page={pageIndex}
        active={currentPage === pageIndex}
        getPageUrl={getPageUrl}
        onChangePage={(_page) => {
          setCurrentPage(_page)
          handleChangePage(_page)
        }}
      />
    )

    // Show all pages when total is small enough that ellipsis is unnecessary
    if (total <= 7) {
      return <>{Array.from({ length: total }, (_, i) => i + 1).map(renderPage)}</>
    }

    // Window: current ±2, expanded towards boundaries to always show at least 5 pages
    const windowStart = Math.max(1, Math.min(currentPage - 2, total - 4))
    const windowEnd = Math.min(total, Math.max(currentPage + 2, 5))

    // Middle pages, excluding always-shown page 1 and last page
    const rangeStart = Math.max(2, windowStart)
    const rangeEnd = Math.min(total - 1, windowEnd)
    const middlePages =
      rangeStart <= rangeEnd
        ? Array.from({ length: rangeEnd - rangeStart + 1 }, (_, i) => rangeStart + i)
        : []

    return (
      <>
        {renderPage(1)}
        {windowStart > 2 && (
          <span className={styles.ellipsis}>
            <FaEllipsis size={20} color="#0B6AB3" />
          </span>
        )}
        {middlePages.map(renderPage)}
        {windowEnd < total - 1 && (
          <span className={styles.ellipsis}>
            <FaEllipsis size={20} color="#0B6AB3" />
          </span>
        )}
        {renderPage(total)}
      </>
    )
  }, [currentPage, handleChangePage, total, router.asPath, router.query, router.locale])

  if (total <= 1) return null

  return (
    <div className={styles.container}>
      {currentPage === 1 ? (
        <span className={`${styles.chevronButton} ${styles.disabled}`}>
          <FaChevronLeft size={13} color="#e4e4e4" />
        </span>
      ) : (
        <Link href={getPageUrl(currentPage - 1)} passHref>
          <a
            className={styles.chevronButton}
            onClick={(e) => {
              e.preventDefault()
              const _page = currentPage - 1
              setCurrentPage(_page)
              handleChangePage(_page)
            }}
          >
            <FaChevronLeft size={13} color="#0B6AB3" />
          </a>
        </Link>
      )}
      {renderPaging}
      {currentPage === total ? (
        <span className={`${styles.chevronButton} ${styles.disabled}`}>
          <FaChevronRight size={13} color="#e4e4e4" />
        </span>
      ) : (
        <Link href={getPageUrl(currentPage + 1)} passHref>
          <a
            className={styles.chevronButton}
            onClick={(e) => {
              e.preventDefault()
              const _page = currentPage + 1
              setCurrentPage(_page)
              handleChangePage(_page)
            }}
          >
            <FaChevronRight size={13} color="#0B6AB3" />
          </a>
        </Link>
      )}
    </div>
  )
}

type PageProps = {
  page: number
  active: boolean
  getPageUrl: (page: number) => string
  onChangePage: (page: number) => void
}

const Page: FC<PageProps> = ({ page, active, getPageUrl, onChangePage }) => {
  return (
    <Link href={getPageUrl(page)} passHref>
      <a
        className={`${styles.pageContainer} ${active ? styles.pageActiveContainer : ''}`}
        onClick={(e) => {
          e.preventDefault()
          onChangePage(page)
        }}
        aria-current={active ? 'page' : undefined}
      >
        <p>{page}</p>
      </a>
    </Link>
  )
}
